import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { getDefaultDateRange, type DateRange } from '@/lib/dateUtils'
import { useRestaurant } from '@/context/RestaurantContext'
import { getTimezoneForCountry } from '@/lib/countries'
import type { BusinessHours } from '@/lib/businessDay'

/** The restaurant's business hours stamped with its country's timezone. */
function withTimezone(restaurant: { businessHours?: BusinessHours; country?: string } | null | undefined): BusinessHours | undefined {
  if (!restaurant?.businessHours) return undefined
  return { ...restaurant.businessHours, timezone: getTimezoneForCountry(restaurant.country) }
}

/**
 * Seeds a report screen's date range with today's *business day* default.
 *
 * currentRestaurant (and its businessHours) loads asynchronously after mount,
 * so a plain `useState(getDefaultDateRange(currentRestaurant?.businessHours))`
 * initializer always runs with businessHours still undefined and freezes on
 * the legacy calendar-day default forever. This hook corrects the range
 * every time businessHours changes (including edits made from Settings while
 * the report screen stays mounted), without clobbering any range the user
 * has since picked manually — tracked via `userModified`, set only when the
 * returned setter is called from outside this hook (i.e. by the screen's own
 * date-picker UI), not by the auto-seed effect below.
 */
export function useBusinessDayDefaultRange(): [DateRange, Dispatch<SetStateAction<DateRange>>] {
  const { currentRestaurant } = useRestaurant()
  const [dateRange, setDateRangeInternal] = useState<DateRange>(() => getDefaultDateRange(currentRestaurant?.businessHours))
  const userModified = useRef(false)

  useEffect(() => {
    if (currentRestaurant?.businessHours && !userModified.current) {
      setDateRangeInternal(getDefaultDateRange(withTimezone(currentRestaurant)))
    }
  }, [currentRestaurant?.businessHours, currentRestaurant?.country])

  const setDateRange: Dispatch<SetStateAction<DateRange>> = (value) => {
    userModified.current = true
    setDateRangeInternal(value)
  }

  return [dateRange, setDateRange]
}
